import { ServiceCategory, ServiceGroup, ServiceItem } from 'orderme-api-integration-client';
import { useNavigate, NavigateOptions } from 'react-router';
import { AppStyle } from '../helpers/appStyle';
import { appStyleMode } from '../helpers/clientConfigs';
import { updateDocumentTitle } from '../helpers/documentHelper';
import { RouterParam } from '../helpers/routerParam';
import { isGoodsCategory } from '../helpers/servicesHelper';
import { generateSlug } from '../helpers/slugGenerator';
import { store } from '../redux/store';
import * as Routes from './allRoutes';
import { RouteConfig } from './routeConfig';

export class RouteNavigatorService {
  private navigate: ReturnType<typeof useNavigate>;

  constructor(navigate: ReturnType<typeof useNavigate>) {
    this.navigate = navigate;
  }

  goBack() {
    this.navigate(-1);
  }
  goTo(to: RouteConfig, options?: NavigateOptions) {
    this.navigateTo(to, options);
  }
  goToPath = (path: string, options?: NavigateOptions) => {
    // Reset document title
    updateDocumentTitle();
    this.navigate(path, options);
  };
  goHome = (replace?: boolean, searchParams?: string) => {
    const options = this.getNavigationOptions(replace);
    this.navigateTo(this.getHomeRoute(), options, searchParams);
  };
  getHomePath = () => this.getRoutePath(this.getHomeRoute(), null, null);
  getHomeRoute = () => (appStyleMode === AppStyle.Mobile ? Routes.MobileHomeRoute : Routes.HomeRoute);
  goToSearch = () => this.navigateTo(Routes.SearchRoute);
  goToCart = (options?: NavigateOptions) => this.navigateTo(Routes.CartRoute, options);
  goToAccount = () => this.navigateTo(Routes.AccountRoute);
  goToRegistration = (options?: NavigateOptions) => this.navigateTo(Routes.RegistrationRoute, options);
  goToAchievements = () => this.navigateTo(Routes.AchievementsRoute);
  goToRules = () => this.navigateTo(Routes.RulesRoute);
  getRulesPath = () => this.getRoutePath(Routes.RulesRoute, null, null);
  goToPrivacyPolicy = () => this.navigateTo(Routes.PrivacyPolicyRoute);
  goToSalesPoints = () => this.navigateTo(Routes.SalesPointsRoute);
  goToSalesPoint = (options?: NavigateOptions) => this.navigateTo(Routes.SalesPointRoute, options);
  goToPartners = () => this.navigateTo(Routes.PartnersRoute);
  goToNews = () => this.navigateTo(Routes.NewsRoute);
  goToNewsItem = (id: string, title: string) => {
    const links = store.getState().linksState.news;
    const link = links.find((link) => link.dataId === id);
    if (link) {
      title = generateSlug(title);
      this.navigateTo(Routes.NewsItemRoute, null, `id=${link.id}`, { title });
    }
  };
  goToAboutUs = () => this.navigateTo(Routes.AboutUsRoute);
  goToWhereTo = () => this.navigateTo(Routes.WhereToRoute);
  goToAddressBook = (options?: NavigateOptions) => this.navigateTo(Routes.AddressBookRoute, options);
  goToAccountSettings = (options?: NavigateOptions) => this.navigateTo(Routes.AccountSettingsRoute, options);
  goToCredits = (credits?: number, referenceId?: string, replace?: boolean) => {
    const options = this.getNavigationOptions(replace);
    var searchParams: string;
    if (credits) {
      searchParams = `${RouterParam.Credits}=${credits}`;
    }
    if (referenceId) {
      if (searchParams) {
        searchParams += `&${RouterParam.Reference}=${referenceId}`;
      } else {
        searchParams = `${RouterParam.Reference}=${referenceId}`;
      }
    }
    this.navigateTo(Routes.CreditsRoute, options, searchParams);
  };
  goToCreditsPayments = (options?: NavigateOptions) => this.navigateTo(Routes.CreditsPaymentsRoute, options);
  goToOrderCheckout = (options?: NavigateOptions) => this.navigateTo(Routes.OrderCheckoutRoute, options);
  goToOrderHistory = (page?: number, replace?: boolean) => {
    const options = this.getNavigationOptions(replace);
    var searchParams: string;
    if (page) {
      searchParams = `page=${page}`;
    }

    this.navigateTo(Routes.OrderHistoryRoute, options, searchParams);
  };
  goToOrderDetail = (id: string) => this.navigateTo(Routes.OrderDetailRoute, null, `id=${id}`);
  goToLogin = (options?: NavigateOptions) => this.navigateTo(Routes.LoginRoute, options);
  goToSupport = (options?: NavigateOptions) => this.navigateTo(Routes.SupportRoute, options);

  goToCategory = (category: ServiceCategory, options?: NavigateOptions) => {
    const path = this.getCategoryPath(category);
    updateDocumentTitle(category.name);
    this.navigate(path, options);
  };

  getCategoryPath = (category: ServiceCategory) => {
    if (isGoodsCategory(category)) {
      return this.getRoutePath(Routes.GoodsRoute, null, null);
    }

    const links = store.getState().linksState.categories;
    const link = links.find((link) => link.dataId === category.id);
    const name = link ? generateSlug(category.name) : '';
    return this.getRoutePath(Routes.CategoryRoute, null, {
      category: name,
      categoryId: link.id,
    });
  };
  goToCategoryGroup = (category: ServiceCategory, group: ServiceGroup, replace?: boolean) => {
    const groupLinks = store.getState().linksState.groups;
    const categoryLinks = store.getState().linksState.categories;
    const categoryLink = categoryLinks.find((link) => link.dataId === category.id);
    const groupLink = groupLinks.find((link) => link.dataId === group.id);
    const categoryName = generateSlug(category.name);
    const groupName = generateSlug(group.name);
    if (categoryLink && groupLink) {
      const options = this.getNavigationOptions(replace);
      this.navigateTo(
        Routes.CategoryGroupRoute,
        options,
        `group=${groupLink.id}`,
        {
          category: categoryName,
          group: groupName,
          categoryId: categoryLink.id,
        },
        group.name
      );
    }
  };
  goToInfo = () => this.navigateTo(Routes.InfoRoute);

  goToFaq = () => this.navigateTo(Routes.FaqRoute);

  goToGoods = () => this.navigateTo(Routes.GoodsRoute);

  goToGoodsItem = (service: ServiceItem) => {
    const links = store.getState().linksState.physicalItems;
    const link = links.find((link) => link.dataId === service.id);
    if (link) {
      const name = generateSlug(service.name);
      this.navigateTo(Routes.GoodsItemRoute, null, `id=${link.id}`, { name }, service.name);
    }
  };

  getGoodsPath = () => this.getRoutePath(Routes.GoodsRoute, null, null);

  getServicesPath = () => this.getRoutePath(Routes.ServicesRoute, null, null);

  goToCreditsItem = (service: ServiceItem) => {
    const links = store.getState().linksState.serviceItems;
    const link = links.find((link) => link.dataId === service.id);
    if (link) {
      const name = generateSlug(service.name);
      this.navigateTo(Routes.CreditsItemRoute, null, `id=${link.id}`, { name }, service.name);
    }
  };

  private navigateTo = (
    to: RouteConfig,
    options?: NavigateOptions,
    searchParams?: string,
    navigationParams?: { [key: string]: string },
    title?: string
  ) => {
    var path = this.getRoutePath(to, searchParams, navigationParams);
    if (!options) {
      options = this.getNavigationOptions();
    }

    if (!title) {
      title = to.title;
    }
    updateDocumentTitle(title);
    this.navigate(path, options);
  };

  private getRoutePath = (to: RouteConfig, searchParams: string, navigationParams: { [key: string]: string }) => {
    var path = appStyleMode === AppStyle.Mobile ? to.path.lt : to.getPath();

    if (searchParams) {
      path += `?${searchParams}`;
    }

    if (navigationParams) {
      Object.keys(navigationParams).forEach((key) => {
        path = path.replace(`:${key}`, navigationParams[key]);
      });
    }
    return path;
  };

  private getNavigationOptions = (replace?: boolean) => {
    const options: NavigateOptions = {};
    if (replace) {
      options.replace = true;
    }

    return options;
  };
}
